import { FormEvent, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { ArrowRightIcon, PlusIcon } from 'app/common/presentation/icons'
import { useConnections } from 'app/connections/application'
import { useEvents } from 'app/events/application'
import { useObjectives } from 'app/objectives/application'
import { ObjectiveCalculation } from 'app/objectives/presentation/components'
import {
  ObjectiveAggregation,
  ObjectiveAggregationType,
  ObjectiveDirection,
  StoreObjectiveContent,
} from 'app/objectives/types'

import { Button, Input, PageBanner, SegmentedControl } from 'components'

import styles from './CreateObjective.module.scss'
import EventInput from './EventInput'
import FilterRow from './FilterRow'
import { FilterDraft, createFilterDraft, isFilterDraftValid, toObjectiveFilter } from './filter'

interface FormSectionProps extends PropsWithChildren {
  allowOverflow?: boolean
  description: string
  title: string
}

const FormSection = ({ allowOverflow, children, description, title }: FormSectionProps) => (
  <section className={styles.formSection}>
    <div className={styles.sectionLabel}>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
    <div className={`${styles.sectionCard} ${allowOverflow ? styles.allowOverflow : ''}`}>
      {children}
    </div>
  </section>
)

interface FieldProps extends PropsWithChildren {
  description?: string
  label: string
}

const Field = ({ children, description, label }: FieldProps) => (
  <div className={styles.field}>
    <div className={styles.fieldLabel}>
      <label>{label}</label>
      {description && <span>{description}</span>}
    </div>
    <div className={styles.fieldControl}>{children}</div>
  </div>
)

const CreateObjective = () => {
  const navigate = useNavigate()

  const { connections } = useConnections()
  const { getEvents } = useEvents()
  const { storeObjective } = useObjectives()
  const { t } = useTranslation('objectives')

  const [availableEventNames, setAvailableEventNames] = useState<string[] | null>(null)
  const [aggregationType, setAggregationType] = useState<ObjectiveAggregationType>('count')
  const [direction, setDirection] = useState<ObjectiveDirection>('maximize')
  const [eventNames, setEventNames] = useState<string[]>([])
  const [filters, setFilters] = useState<FilterDraft[]>([])
  const [draggedFilterId, setDraggedFilterId] = useState<number | null>(null)

  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [targetTrait, setTargetTrait] = useState('')

  const aggregation: ObjectiveAggregation =
    aggregationType === 'count'
      ? { type: 'count' }
      : { targetTrait: targetTrait.trim(), type: 'value' }

  const nextFilterId = useRef(0)

  useEffect(() => {
    void getEvents().then((events) => {
      setAvailableEventNames([...new Set(events)].sort())
    })
  }, [getEvents])

  const hasConnections = connections.length > 0

  if (!hasConnections || (availableEventNames !== null && availableEventNames.length === 0)) {
    return <Navigate to={'/objectives'} replace />
  }

  const canSubmit =
    name.trim().length > 0 &&
    eventNames.length > 0 &&
    (aggregationType === 'count' || targetTrait.trim().length > 0) &&
    filters.every(isFilterDraftValid)

  const addFilter = () => {
    nextFilterId.current += 1
    setFilters((current) => [...current, createFilterDraft(nextFilterId.current)])
  }

  const updateFilter = (updatedFilter: FilterDraft) =>
    setFilters((current) =>
      current.map((filter) => (filter.id === updatedFilter.id ? updatedFilter : filter)),
    )

  const removeFilter = (id: number) =>
    setFilters((current) => current.filter((filter) => filter.id !== id))

  const moveFilter = (targetId: number) => {
    if (draggedFilterId === null || draggedFilterId === targetId) return

    setFilters((current) => {
      const sourceIndex = current.findIndex((filter) => filter.id === draggedFilterId)
      const targetIndex = current.findIndex((filter) => filter.id === targetId)
      if (sourceIndex < 0 || targetIndex < 0) return current

      const reordered = [...current]
      const [movedFilter] = reordered.splice(sourceIndex, 1)
      reordered.splice(targetIndex, 0, movedFilter)
      return reordered
    })
    setDraggedFilterId(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return

    const content: StoreObjectiveContent = {
      aggregation,
      direction,
      events: eventNames,
      filters: filters.map(toObjectiveFilter),
      isReadyForTraining: true,
      name: name.trim(),
      per: 'user',
    }

    setSubmitting(true)
    const result = await storeObjective(content)
    setSubmitting(false)

    if (result.ok) {
      void navigate(`/objectives/${result.val.id}`)
    }
  }

  if (availableEventNames === null) return null

  return (
    <>
      <form className={styles.createObjective} onSubmit={(event) => void handleSubmit(event)}>
        <PageBanner subtitle={t('description')} title={t('create.title')} />

        <div className={styles.formBody}>
          <FormSection
            description={t('create.basics.description')}
            title={t('create.basics.title')}
          >
            <Field
              description={t('create.fields.name.description')}
              label={t('create.fields.name.label')}
            >
              <Input onChange={setName} value={name} />
            </Field>
          </FormSection>

          <FormSection
            description={t('create.target.description')}
            title={t('create.target.title')}
          >
            <Field
              description={t('create.fields.events.description')}
              label={t('create.fields.events.label')}
            >
              <EventInput
                addLabel={t('create.fields.events.add')}
                eventNames={availableEventNames ?? []}
                onChange={setEventNames}
                removeLabel={t('create.fields.events.remove')}
                value={eventNames}
              />
            </Field>

            <Field
              description={t('create.fields.aggregation.description')}
              label={t('create.fields.aggregation.label')}
            >
              <SegmentedControl
                className={styles.segmentedControl}
                onChange={setAggregationType}
                options={[
                  { label: t('create.aggregation.count'), value: 'count' },
                  { label: t('create.aggregation.value'), value: 'value' },
                ]}
                value={aggregationType}
              />
            </Field>

            <div className={styles.inlineFields}>
              {aggregationType === 'value' && (
                <Input
                  label={t('create.fields.targetTrait.label')}
                  onChange={setTargetTrait}
                  value={targetTrait}
                />
              )}
              <Input label={t('create.fields.per.label')} mandatory value={t('create.per.user')} />
            </div>

            <Field
              description={t('create.fields.direction.description')}
              label={t('create.fields.direction.label')}
            >
              <SegmentedControl
                className={styles.segmentedControl}
                onChange={setDirection}
                options={[
                  { label: t('create.direction.maximize'), value: 'maximize' },
                  { label: t('create.direction.minimize'), value: 'minimize' },
                ]}
                value={direction}
              />
            </Field>

            <div className={styles.preview}>
              <span className={styles.previewLabel}>{t('create.preview')}</span>
              <div className={styles.formula}>
                <ObjectiveCalculation aggregation={aggregation} events={eventNames} per="user" />
                <ArrowRightIcon />
                <span>{direction}</span>
              </div>
            </div>
          </FormSection>
        </div>

        <div className={styles.formBody}>
          <FormSection
            allowOverflow
            description={t('create.filters.description')}
            title={t('create.filters.title')}
          >
            <div className={styles.filtersContent}>
              <div className={styles.filtersHeader}>
                <span>{t('create.filters.title')}</span>
                <Button onClick={addFilter} rightIcon={<PlusIcon />} small variant="secondary">
                  {t('create.filters.add')}
                </Button>
              </div>

              {filters.length > 0 && (
                <div className={styles.filterList}>
                  {filters.map((filter) => (
                    <FilterRow
                      dragging={draggedFilterId === filter.id}
                      filter={filter}
                      key={filter.id}
                      onChange={updateFilter}
                      onDragEnd={() => setDraggedFilterId(null)}
                      onDragStart={() => setDraggedFilterId(filter.id)}
                      onDrop={() => moveFilter(filter.id)}
                      onRemove={() => removeFilter(filter.id)}
                      options={availableEventNames}
                    />
                  ))}
                </div>
              )}
            </div>
          </FormSection>
        </div>

        <footer className={styles.footer}>
          <Button disabled={!canSubmit} loading={submitting} type="submit">
            {t('create.actions.submit')}
          </Button>
        </footer>
      </form>
    </>
  )
}

export default CreateObjective
